import React, { useState, useRef, useEffect } from 'react';
import { convertTextToSpeech } from '../utils/apiHandler';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import { enhanceTextForTTS } from "../utils/helpers"

const AudioPlayer = ({ text, autoPlay = true }) => {
    const {
        getIdTokenClaims,
        getAccessTokenWithPopup,
        logout,
        isAuthenticated
    } = useAuth0();

    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const audioRef = useRef(null);

    const cleanupAudio = () => {
        if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
        }
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
    };

    useEffect(() => {
        if (text && text.trim() !== '' && autoPlay && isAuthenticated) {
            loadAndPlay();
        }

        return () => {
            cleanupAudio();
        };
    }, [text, isAuthenticated]);

    const loadAndPlay = async () => {
        if (isLoading || !text || text.trim() === '') return;

        if (!isAuthenticated) {
            return;
        }

        if (audioUrl && audioRef.current) {
            audioRef.current.play();
            return;
        }

        setIsLoading(true);

        try {
            const inputText = enhanceTextForTTS(text, true);
            const audioBlob = await convertTextToSpeech(
                getIdTokenClaims,
                getAccessTokenWithPopup,
                logout,
                inputText
            );

            if (audioBlob) {
                if (audioUrl) {
                    URL.revokeObjectURL(audioUrl);
                }

                const url = URL.createObjectURL(audioBlob);
                setAudioUrl(url);

                const audio = new Audio(url);
                audioRef.current = audio;

                audio.onplay = () => setIsPlaying(true);
                audio.onended = () => setIsPlaying(false);
                audio.onpause = () => setIsPlaying(false);
                audio.onerror = (e) => {
                    console.error('Audio playback error:', e);
                    setIsPlaying(false);
                    setIsLoading(false);
                };

                const playPromise = audio.play();

                if (playPromise !== undefined) {
                    playPromise.catch(error => {
                        console.error('Autoplay prevented:', error);
                        setIsPlaying(false);
                    });
                }
            }
        } catch (error) {
            console.error('Error playing audio:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const togglePlay = () => {
        if (isLoading) return;

        if (!isAuthenticated) {
            return;
        }

        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                const playPromise = audioRef.current.play();

                if (playPromise !== undefined) {
                    playPromise.catch(error => {
                        loadAndPlay();
                    });
                }
            }
        } else {
            loadAndPlay();
        }
    };

    if (!isAuthenticated) {
        return null;
    }

    return (
        <button
            onClick={togglePlay}
            disabled={isLoading}
            className="audio-player-button"
            aria-label={isPlaying ? 'Pause speech' : 'Play speech'}
        >
            {isLoading ? (
                // Loading spinner (Tailwind Heroicons)
                <svg className="audio-loading-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            ) : isPlaying ? (
                // Pause icon (Tailwind Heroicons - Solid)
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-5">
                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12ZM9 8.25a.75.75 0 0 0-.75.75v6c0 .414.336.75.75.75h.75a.75.75 0 0 0 .75-.75V9a.75.75 0 0 0-.75-.75H9Zm5.25 0a.75.75 0 0 0-.75.75v6c0 .414.336.75.75.75H15a.75.75 0 0 0 .75-.75V9a.75.75 0 0 0-.75-.75h-.75Z" clipRule="evenodd" />
                </svg>
            ) : (
                // Play icon (Tailwind Heroicons - Solid)
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-5">
                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm14.024-.983a1.125 1.125 0 0 1 0 1.966l-5.603 3.113A1.125 1.125 0 0 1 9 15.113V8.887c0-.857.921-1.4 1.671-.983l5.603 3.113Z" clipRule="evenodd" />
                </svg>
            )}
        </button>
    );
};

export default AudioPlayer;